import React from 'react';
import { motion } from 'framer-motion';
import {
  FiFilm,
  FiCamera,
  FiMusic,
  FiGlobe,
  FiUsers,
  FiStar,
  FiArrowRight,
  FiMonitor,
  FiUserCheck,
  FiEdit3,
} from 'react-icons/fi';

const services = [
  {
    id: 'film-production',
    icon: FiFilm,
    title: 'Film Production',
    desc: 'End-to-end feature film production from concept to silver screen. We manage every frame with world-class precision.',
    accent: '#f37021',
  },
  {
    id: 'direction-cinematography',
    icon: FiCamera,
    title: 'Direction & Cinematography',
    desc: 'Award-winning directors and DPs who breathe life into scripts through stunning visual language and composition.',
    accent: '#D4AF37',
  },
  {
    id: 'vfx-post',
    icon: FiStar,
    title: 'VFX & Post Production',
    desc: 'State-of-the-art visual effects, colour grading and post-production pipelines that deliver Hollywood-grade results.',
    accent: '#f37021',
  },
  {
    id: 'original-score-sound',
    icon: FiMusic,
    title: 'Original Score & Sound',
    desc: 'Bespoke musical compositions and immersive sound design that transform every scene into an emotional journey.',
    accent: '#D4AF37',
  },
  {
    id: 'global-distribution',
    icon: FiGlobe,
    title: 'Global Distribution',
    desc: 'Strategic distribution across theatrical, streaming and international markets to ensure maximum reach and impact.',
    accent: '#f37021',
  },
  {
    id: 'talent-casting',
    icon: FiUsers,
    title: 'Talent & Casting',
    desc: 'Access to an elite network of actors, performers and creative talent, matched perfectly to every production.',
    accent: '#D4AF37',
  },
  {
    id: 'class-direction',
    icon: FiMonitor,
    title: 'Direction Masterclass',
    desc: 'Learn the art of cinematic storytelling and visual composition from industry-leading visionary directors.',
    accent: '#f37021',
  },
  {
    id: 'class-acting',
    icon: FiUserCheck,
    title: 'Acting Masterclass',
    desc: 'Immersive acting workshops designed to bring out authentic, powerful performances on screen and stage.',
    accent: '#D4AF37',
  },
  {
    id: 'class-screenplay',
    icon: FiEdit3,
    title: 'Screenplay Writing',
    desc: 'Master the craft of scriptwriting, from concept generation to drafting compelling, industry-standard screenplays.',
    accent: '#f37021',
  },
];

export default function Services({ onServiceClick }) {
  return (
    <section id="services" className="relative py-28 bg-brand-dark2 overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />

      {/* Background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-orange/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-gold/4 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-20"
        >
          <span className="section-label block mb-4">What We Offer</span>
          <h2 className="font-cinzel font-black text-4xl md:text-5xl text-white mb-5">
            Our <span className="text-gold-gradient">Services</span>
          </h2>
          <div className="gold-divider mb-6" />
          <p className="font-inter text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            A complete suite of cinematic services, delivering excellence at
            every stage of production — from the first idea to the final credits.
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="glass-card p-8 rounded-sm group relative overflow-hidden cursor-pointer"
                onClick={() => onServiceClick && onServiceClick(svc.id)}
                whileHover={{ y: -4, boxShadow: `0 24px 60px ${svc.accent}18` }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Hover gradient sweep */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 20% 20%, ${svc.accent}10 0%, transparent 70%)`,
                  }}
                />

                {/* Icon */}
                <div
                  className="relative w-14 h-14 rounded-sm flex items-center justify-center mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${svc.accent}22, ${svc.accent}08)`,
                    border: `1px solid ${svc.accent}30`,
                  }}
                >
                  <Icon size={24} color={svc.accent} />
                </div>

                {/* Number */}
                <div
                  className="absolute top-6 right-6 font-cinzel font-black text-5xl opacity-5 group-hover:opacity-10 transition-opacity duration-400"
                  style={{ color: svc.accent }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>

                <h3 className="font-cinzel font-bold text-white text-lg mb-3 group-hover:text-brand-gold transition-colors duration-300">
                  {svc.title}
                </h3>

                <p className="font-inter text-white/50 text-sm leading-relaxed mb-5">
                  {svc.desc}
                </p>

                {/* Explore link */}
                <div className="flex items-center gap-2 font-cinzel text-[10px] tracking-widest uppercase transition-all duration-300"
                  style={{ color: `${svc.accent}70` }}>
                  <span className="group-hover:text-brand-gold transition-colors">Explore Service</span>
                  <FiArrowRight size={10} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>

                {/* Bottom accent line */}
                <div
                  className="absolute bottom-0 left-0 h-0.5 w-0 group-hover:w-full transition-all duration-500"
                  style={{ background: `linear-gradient(90deg, ${svc.accent}, transparent)` }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20"
        >
          {[
            { value: '120+', label: 'Films Produced' },
            { value: '45+',  label: 'Awards Won' },
            { value: '28',   label: 'Countries Reached' },
            { value: '15+',  label: 'Years of Excellence' },
          ].map((stat, i) => (
            <div key={i} className="text-center border border-brand-gold/10 py-8 rounded-sm hover:border-brand-gold/30 transition-colors duration-300">
              <div className="font-cinzel font-black text-4xl text-gold-gradient mb-2">
                {stat.value}
              </div>
              <div className="font-inter text-white/40 text-xs tracking-widest uppercase">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
